import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled, { css } from 'styled-components';
import { AutoScaledImage } from '@helpcenter/auto-scaled-image';
import { isIsomorphicDompurifyLibUsed } from '@helpcenter/feature-flags';
import { InteractiveElement } from '@helpcenter/interactive-element';
import * as bp from '@helpcenter/styles/src/breakpoints';
import * as fonts from '@helpcenter/styles/src/fonts';
import * as grid from '@helpcenter/styles/src/grid';
import * as mixins from '@helpcenter/styles/src/mixins';
import * as transitions from '@helpcenter/styles/src/transitions';
import * as zIndex from '@helpcenter/styles/src/z-index';
import domPurify from 'dompurify';
import * as DOMPurify from 'isomorphic-dompurify';
import { di } from 'react-magnetic-di';
import { useRouterActions } from 'react-resource-router';
import { useIsPageInEditMode } from 'view/layout-next/controllers';
import { AnalyticsContext, useAnalyticsEvents } from '@atlaskit/analytics-next';
import { colors } from '@atlaskit/theme';
import { N50A, N40A } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { getEnv } from '@atlassian/help-center-common-util/env';
import { open } from '@atlassian/help-center-common-util/location';
import { cardStyle } from './styled';

interface PortalCardProps {
    id: number;
    // index of this portal in popular portal list view
    index?: number;
    portalName: string;
    description?: string;
    portalUrl: string;
    iconUrl?: string;
    onClick?: (index?: number) => void;
    openInNewTab?: boolean;
}

export const customPurify = domPurify();
export const customPurifyIsomorphic = DOMPurify;

customPurify.addHook?.('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' || node.tagName === 'BUTTON' || node.tagName === 'INPUT') {
        node.setAttribute('tabIndex', '-1');
    }
});

customPurifyIsomorphic.addHook?.('afterSanitizeAttributes', (node: Element) => {
    if (node.tagName === 'A' || node.tagName === 'BUTTON' || node.tagName === 'INPUT') {
        node.setAttribute('tabIndex', '-1');
    }
});

const PortalCard: React.FunctionComponent<PortalCardProps> = ({
    onClick,
    index,
    id,
    portalName,
    description,
    portalUrl,
    iconUrl,
    openInNewTab,
}) => {
    di(useIsPageInEditMode, useRouterActions, useAnalyticsEvents);
    const [isPageInEditMode] = useIsPageInEditMode();
    const { push } = useRouterActions();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const hasIcon = !!iconUrl;
    const hasPortalUrl = !!portalUrl;
    const hasDescription = !!description;
    const hasOnlyPortalName = !description && !iconUrl;

    const memoizedOnClick = React.useCallback(() => {
        const event = createAnalyticsEvent({
            analyticsType: UI_EVENT_TYPE,
            action: 'clicked',
            actionSubjectId: 'portal',
        });
        event.context.push({
            componentName: 'link',
            attributes: {
                index,
                hasIcon,
                hasPortalUrl,
                hasDescription,
                portalId: id,
            },
        });

        event.fire();

        if (openInNewTab) {
            const { baseUrl } = getEnv();
            open(`${baseUrl}${portalUrl}`, '_blank');
        } else {
            push(portalUrl) as void;
        }

        onClick && onClick(index);
    }, [
        createAnalyticsEvent,
        index,
        hasIcon,
        hasPortalUrl,
        hasDescription,
        id,
        push,
        portalUrl,
        onClick,
        openInNewTab,
    ]);

    let sanitizedDescription: string = '';

    if (isIsomorphicDompurifyLibUsed()) {
        sanitizedDescription = customPurifyIsomorphic.sanitize(description || '');
    } else {
        sanitizedDescription = customPurify.sanitize(description || '');
    }

    const content = (
        <Container hasIcon={hasIcon}>
            {!!iconUrl && (
                <IconContainer>
                    <AutoScaledImage src={iconUrl} alt={''} draggable="false" />
                </IconContainer>
            )}
            <Content>
                <PortalName hasOnlyPortalName={hasOnlyPortalName} title={portalName}>
                    {portalName}
                </PortalName>
                {!!description && (
                    <Description
                        title={description}
                        dangerouslySetInnerHTML={{
                            __html: sanitizedDescription,
                        }}
                    />
                )}
            </Content>
        </Container>
    );

    const portalCardContent = (
        <CardRoot aria-label={portalName} onClick={memoizedOnClick} data-test-id={`portal:${portalName}`} role="link">
            {content}
        </CardRoot>
    );

    return (
        <AnalyticsContext
            data={{
                attributes: {
                    index,
                    hasIcon,
                    hasPortalUrl,
                    hasDescription,
                    portalId: id,
                },
            }}
        >
            {isPageInEditMode ? <RootWhenEditingLayout>{content}</RootWhenEditingLayout> : portalCardContent}
        </AnalyticsContext>
    );
};

export default PortalCard;

interface PortalNameProps {
    hasOnlyPortalName: boolean;
}

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const PortalName = styled.div<PortalNameProps>`
    ${fonts.h400};
    /* setting max-width 100% because on IE, text flows out of the container */
    max-width: 100%;
    margin-top: 0;
    margin-bottom: ${token('space.050', '4px')};

    ${(props) => {
        const lines = props.hasOnlyPortalName ? 3 : 1;
        const lineHeight = grid.multiple(2).unitless;
        return mixins.multiLineClamp(lines, lineHeight);
    }};

    ${bp.fromXSmall.css`
        ${fonts.h500};
        margin-top: 0;
        margin-bottom: ${token('space.100', '8px')};
        max-height: ${(props) => (props.hasOnlyPortalName ? grid.multiple(7.5).px : grid.multiple(2.5).px)};
    `};

    /* Because of AK now sets the color in the font-size mixins (inside a media query) we need to */
    /* define our color with a higher specicifity. */
    /* stylelint-disable-next-line no-duplicate-selectors */
    & {
        color: ${(props) => token('color.text.brand', props.theme.color)};
    }
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Description = styled.div`
    ${fonts.xsmall};
    ${mixins.multiLineClamp(3, grid.multiple(2.25).unitless)};
    /* setting max-width 100% because on IE, text flows out of the container */
    max-width: 100%;
    color: ${token('color.text', colors.N800)};
    line-height: ${grid.multiple(2.25).px};

    ${bp.fromXSmall.css`
        ${fonts.regular};
        line-height: ${grid.multiple(2.5).px};
        max-height: ${grid.multiple(7.5).px};
    `};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Content = styled.div`
    overflow: hidden;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled
const CardRoot = styled(InteractiveElement)`
    ${cardStyle}
    ${mixins.raiseUpOnHoverOrFocus()};
    background-color: ${token('elevation.surface.raised', colors.N0)};
    transition: transform ${transitions.speedMs.fast}ms ${transitions.timingFunctions.easeInOut};

    &:hover,
    &:focus {
        z-index: ${zIndex.portalCardLayout};
        text-decoration: none;
    }
    cursor: pointer;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const RootWhenEditingLayout = styled.div`
    ${cardStyle}
    box-shadow: ${token('elevation.shadow.raised', `0 1px 1px ${N50A}, 0 0 1px 1px ${N40A}`)};
    background-color: ${token('elevation.surface.raised', colors.N0)};
    transition: transform ${transitions.speedMs.fast}ms ${transitions.timingFunctions.easeInOut};

    &:hover,
    &:focus {
        z-index: ${zIndex.portalCardLayout};
        text-decoration: none;
    }
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const IconContainer = styled.div`
    ${mixins.flexCenter};
    height: ${grid.multiple(5).px};
    width: ${grid.multiple(5).px};
    flex-shrink: 0;
    margin-right: ${token('space.200', '16px')};

    ${bp.fromXSmall.css`
        height: ${grid.multiple(6).px};
        width: ${grid.multiple(6).px};
    `};
`;

// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const largePadding = css`
    padding: ${token('space.200', '16px')} ${token('space.300', '24px')}; /* check with Kate for padding top and bottom */

    ${bp.fromXSmall.css`
        padding: ${token('space.300', '24px')} ${token('space.400', '32px')};
    `};
`;

// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const smallPadding = css`
    padding: ${token('space.200', '16px')} ${token('space.200', '16px')}; /* check with Kate for padding top and bottom */

    ${bp.fromXSmall.css`
        padding: ${token('space.300', '24px')} ${token('space.300', '24px')};
    `};
`;

interface ContainerProps {
    hasIcon: boolean;
}

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Container = styled.div<ContainerProps>`
    display: flex;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    align-items: center;
    justify-content: flex-start;

    ${(props) => (props.hasIcon ? smallPadding : largePadding)};
`;
